// ui/src/components/finance/ClosePackagePanel.jsx
/**
 * @file ClosePackagePanel.jsx
 * @module ui/src/components/finance/ClosePackagePanel
 * @description
 *   Generates and displays the immutable evidence package associated with a fiscal
 *   period close. Package artifact identity is deliberately independent of the
 *   PeriodCloseWorkflow gate state.
 *
 * @version 1.0.0
 * @since S756
 * @author data-fit LLC · DecisionFlow · S776
 *
 * @architecture
 *   Locked decisions : #25
 *   Temporal invariants : INV-9
 *   Design principles : DP-10, DP-14
 *   Cross-cutting contracts : XC-7
 *
 * @domain-rules
 *   - closeStatus.allGatesPassed is server authoritative and is never recomputed.
 *   - Package generation never advances or mutates close workflow gate state.
 *   - onGenerate fires only after the server confirms package generation.
 *   - packageId and generatedAt identify immutable audit evidence.
 *   - Existing package existence and workflow completion are independent concepts.
 *
 * @session-6-migration-flags
 *   - POST /api/v1/finance/period/{id}/close-package — Session 6 API gap
 *   - GET /api/v1/finance/period/{id}/close-package/{pkgId} — Session 6 API gap
 *   - settlement entries in package — pending m_tc_12
 *
 * @changelog
 *   1.0.0  S756  Initial build — Group Finance batch delivery
 */

import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';

/**
 * @typedef {Object} CloseStatusPayload
 * @property {boolean} allGatesPassed
 * @property {string[]} failedGates
 * @property {string|null} existingPackageId
 */

/**
 * @typedef {Object} PackageContentSummary
 * @property {number} planVersionCount
 * @property {number} positionCount
 * @property {number} creditEventCount
 * @property {number|string} calculationRunRef
 * @property {number} payoutSummaryCount
 * @property {boolean} settlementEntriesIncluded
 * @property {boolean} drawLedgerIncluded
 * @property {boolean} auditLineageIncluded
 * @property {boolean} payrollExportIncluded
 * @property {boolean} glReconciliationIncluded
 */

/**
 * @typedef {Object} ClosePackageMeta
 * @property {string} packageId
 * @property {string} generatedAt
 * @property {string} calculationRunId
 * @property {string} periodLabel
 * @property {PackageContentSummary} contentSummary
 */

/**
 * @typedef {Object} ClosePackagePanelProps
 * @property {number} fiscalPeriodId
 * @property {CloseStatusPayload} closeStatus
 * @property {Function} onGenerate
 */

const GATE_LABELS = {
  data_readiness: 'Data readiness',
  calculation_complete: 'Calculation complete',
  exceptions_resolved: 'Exceptions resolved',
  settlements_reconciled: 'Settlements reconciled',
  payroll_gl_reconciled: 'Payroll / GL reconciliation',
  period_locked: 'Period locked',
};

const styles = {
  metadata: {
    display: 'grid',
    gap: '0.75rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
  },
  item: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.2rem',
  },
  label: {
    color: 'rgb(var(--color-text-muted))',
    fontSize: '0.75rem',
    fontWeight: 600,
  },
  contents: {
    display: 'grid',
    gap: '0.5rem',
    gridTemplateColumns: 'repeat(auto-fit, minmax(15rem, 1fr))',
  },
  controls: {
    alignItems: 'end',
    display: 'flex',
    flexWrap: 'wrap',
    gap: '0.75rem',
  },
  field: {
    display: 'flex',
    flexDirection: 'column',
    gap: '0.25rem',
  },
  select: {
    background: 'rgb(var(--color-surface))',
    border: '1px solid rgb(var(--color-border))',
    color: 'rgb(var(--color-text))',
    minHeight: '2.25rem',
  },
};

function formatDateTime(value) {
  // WHY: Package generation timestamp is immutable artifact identity and must be
  // rendered exactly as available rather than replaced with a workflow timestamp.
  if (!value) return 'Not available';

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;

  return new Intl.DateTimeFormat(undefined, {
    dateStyle: 'medium',
    timeStyle: 'short',
  }).format(date);
}

function includedText(value) {
  return value ? 'Included' : 'Not included';
}

function ClosePackagePanel({
  fiscalPeriodId,
  closeStatus,
  onGenerate,
}) {
  // WHY: Generation status represents the artifact request lifecycle only; it
  // never mirrors or mutates PeriodCloseWorkflow gate status.
  const [generationStatus, setGenerationStatus] = useState('idle');

  // WHY: Package metadata is immutable evidence returned by the package API.
  const [packageMeta, setPackageMeta] = useState(null);

  // WHY: Export format is a view/download preference and does not modify the package.
  const [exportFormat, setExportFormat] = useState('pdf');

  // WHY: Artifact-generation failure is distinct from close gate failure.
  const [generationError, setGenerationError] = useState('');

  // WHY: Existing-package retrieval has its own failure state and does not invalidate
  // the close workflow's server-authoritative completion state.
  const [loadError, setLoadError] = useState('');

  useEffect(() => {
    const controller = new AbortController();

    async function loadExistingPackage() {
      // WHY: No package ID means package absence; the component must not infer one
      // from allGatesPassed or any workflow status.
      if (!closeStatus.existingPackageId) {
        setPackageMeta(null);
        setLoadError('');
        return;
      }

      setGenerationStatus('generating');
      setLoadError('');

      try {
        // WHY: Existing package identity/content must be fetched from its immutable
        // artifact endpoint rather than reconstructed from close-status data.
        const response = await fetch(
          `/api/v1/finance/period/${fiscalPeriodId}/close-package/${encodeURIComponent(
            closeStatus.existingPackageId,
          )}`,
          {
            method: 'GET',
            headers: { Accept: 'application/json' },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(
            `Existing close package could not be loaded (${response.status}).`,
          );
        }

        const payload = await response.json();
        setPackageMeta(payload);
        setGenerationStatus('complete');
      } catch (requestError) {
        if (requestError.name !== 'AbortError') {
          setLoadError(
            requestError.message || 'Existing close package could not be loaded.',
          );
          setGenerationStatus('error');
        }
      }
    }

    loadExistingPackage();

    // WHY: Changing fiscal period/package identity must cancel obsolete artifact reads.
    return () => controller.abort();
  }, [fiscalPeriodId, closeStatus.existingPackageId]);

  const handleGenerate = async () => {
    // WHY: The server-provided allGatesPassed flag is the only permission signal for
    // package generation; failedGates.length is intentionally not consulted.
    if (!closeStatus.allGatesPassed) {
      return;
    }

    setGenerationStatus('generating');
    setGenerationError('');

    try {
      // WHY: Package creation is a governance mutation and must complete server-side
      // before any local artifact state or parent notification changes.
      const response = await fetch(
        `/api/v1/finance/period/${fiscalPeriodId}/close-package`,
        {
          method: 'POST',
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({}),
        },
      );

      if (!response.ok) {
        throw new Error(
          `Close package generation failed (${response.status}).`,
        );
      }

      const payload = await response.json();

      // WHY: DP-10 prohibits optimistic artifact identity; these values are written
      // only after the server has returned a successful immutable package record.
      setPackageMeta(payload);
      setGenerationStatus('complete');

      // WHY: Parent notification occurs only after server 200 and communicates that
      // package evidence changed, not that any close gate changed.
      onGenerate();
    } catch (requestError) {
      setGenerationStatus('error');
      setGenerationError(
        requestError.message || 'Close package could not be generated.',
      );
    }
  };

  const handleExportFormatChange = (event) => {
    // WHY: Export representation changes only the download format of immutable evidence.
    setExportFormat(event.target.value);
  };

  const content = packageMeta?.contentSummary;

  return (
    <section
      aria-labelledby="close-package-title"
      className="modifier-manager-card"
      style={{ fontFamily: '"IBM Plex Sans", "Segoe UI", Arial, sans-serif' }}
    >
      <div className="modifier-manager-header">
        <div>
          <h3 id="close-package-title">Close package</h3>
          <p>
            Generated audit evidence is independent of the close workflow checklist.
          </p>
        </div>
      </div>

      <div className="modifier-manager-body">
        {/*
         * WHY: Failed gates are named individually so Finance knows what blocks
         * generation; the client does not independently decide whether they constitute
         * an overall failure.
         */}
        {!closeStatus.allGatesPassed ? (
          <div className="modifier-manager-warning" role="status">
            <strong>Close package generation is blocked.</strong>
            <p>The following server-reported gates remain unresolved:</p>
            <ul>
              {closeStatus.failedGates.map((gateKey) => (
                <li key={gateKey}>
                  {GATE_LABELS[gateKey] || gateKey}
                </li>
              ))}
            </ul>
          </div>
        ) : null}

        {/*
         * WHY: Loading/generating state concerns only the package artifact lifecycle.
         */}
        {generationStatus === 'generating' ? (
          <div aria-live="polite" role="status">
            <div className="skeleton">Loading close package evidence…</div>
          </div>
        ) : null}

        {/*
         * WHY: Existing-artifact read failure remains distinct from workflow readiness.
         */}
        {loadError ? (
          <div className="modifier-manager-error-state" role="alert">
            {loadError}
          </div>
        ) : null}

        {/*
         * WHY: Package-generation failure does not roll back or reinterpret close gates.
         */}
        {generationError ? (
          <div className="modifier-manager-inline-error" role="alert">
            {generationError}
          </div>
        ) : null}

        {/*
         * WHY: A server-returned package is immutable evidence with independent identity.
         */}
        {packageMeta ? (
          <>
            <div className="modifier-manager-info" role="note">
              This package is an immutable audit record. Its contents are fixed at the time of generation.
            </div>

            <div style={styles.metadata}>
              <div style={styles.item}>
                <span style={styles.label}>Package ID</span>
                <span>{packageMeta.packageId}</span>
              </div>

              <div style={styles.item}>
                <span style={styles.label}>Generated</span>
                <span>{formatDateTime(packageMeta.generatedAt)}</span>
              </div>

              <div style={styles.item}>
                <span style={styles.label}>Calculation run</span>
                <span>{packageMeta.calculationRunId}</span>
              </div>

              <div style={styles.item}>
                <span style={styles.label}>Period</span>
                <span>{packageMeta.periodLabel}</span>
              </div>
            </div>

            {/*
             * WHY: Content summary describes what was frozen into this package at
             * generation time; it is not interpreted as close workflow state.
             */}
            {content ? (
              <div>
                <h4>Contents</h4>
                <div style={styles.contents}>
                  <span>{content.planVersionCount} plan versions</span>
                  <span>{content.positionCount} positions</span>
                  <span>{content.creditEventCount} credit events</span>
                  <span>{content.payoutSummaryCount} payout summaries</span>
                  <span>
                    Settlement entries:{' '}
                    {includedText(content.settlementEntriesIncluded)}
                  </span>
                  <span>
                    Draw ledger: {includedText(content.drawLedgerIncluded)}
                  </span>
                  <span>
                    Audit lineage: {includedText(content.auditLineageIncluded)}
                  </span>
                  <span>
                    Payroll export: {includedText(content.payrollExportIncluded)}
                  </span>
                  <span>
                    GL reconciliation:{' '}
                    {includedText(content.glReconciliationIncluded)}
                  </span>
                </div>
              </div>
            ) : null}

            <div style={styles.controls}>
              <label style={styles.field}>
                <span style={styles.label}>Export format</span>
                <select
                  onChange={handleExportFormatChange}
                  style={styles.select}
                  value={exportFormat}
                >
                  <option value="pdf">PDF</option>
                  <option value="csv">CSV</option>
                  <option value="json">JSON</option>
                </select>
              </label>

              <a
                className="secondary-button"
                href={`/api/v1/finance/period/${fiscalPeriodId}/close-package/${encodeURIComponent(
                  packageMeta.packageId,
                )}/export?format=${exportFormat}`}
              >
                Download
              </a>
            </div>
          </>
        ) : null}

        {/*
         * WHY: Generation availability uses allGatesPassed directly. Existing package
         * presence changes the action label but does not change the gate determination.
         */}
        {closeStatus.allGatesPassed
        && generationStatus !== 'generating' ? (
          <button
            className="primary-button"
            onClick={handleGenerate}
            type="button"
          >
            {packageMeta || closeStatus.existingPackageId
              ? 'Re-generate'
              : 'Generate close package'}
          </button>
        ) : null}
      </div>
    </section>
  );
}

ClosePackagePanel.propTypes = {
  closeStatus: PropTypes.shape({
    allGatesPassed: PropTypes.bool.isRequired,
    existingPackageId: PropTypes.string,
    failedGates: PropTypes.arrayOf(PropTypes.string).isRequired,
  }).isRequired,
  fiscalPeriodId: PropTypes.number.isRequired,
  onGenerate: PropTypes.func.isRequired,
};

/**
 * @example
 * <ClosePackagePanel
 *   fiscalPeriodId={202608}
 *   closeStatus={{
 *     allGatesPassed: true,
 *     failedGates: [],
 *     existingPackageId: null,
 *   }}
 *   onGenerate={() => refetchCloseStatus()}
 * />
 */

/**
 * DOMAIN COMPLIANCE CHECK
 *
 * ✓ DP-10:
 *   No optimistic package creation occurs. packageMeta and onGenerate change only
 *   after a successful POST response.
 *
 * ✓ INV-9:
 *   packageId and generatedAt are rendered as immutable artifact identity and the
 *   locked immutability notice is shown after package evidence exists.
 *
 * ✓ C-31 / C-56 separation:
 *   This component neither advances nor derives workflow gate state.
 *
 * ✓ XC-7 / DP-14:
 *   Gate blocks, request states, package existence, and content inclusion are textual.
 *
 * ✓ Server authority:
 *   allGatesPassed is used directly and is never recomputed from failedGates.
 *
 * ✓ DP-8:
 *   Prohibited Finance approval language is absent.
 */

/**
 * SESSION 6 MIGRATION FLAGS
 *
 * - POST /api/v1/finance/period/{id}/close-package — Session 6 API gap.
 * - GET /api/v1/finance/period/{id}/close-package/{pkgId} — Session 6 API gap.
 * - Settlement entries included in package — pending m_tc_12.
 */

/**
 * ACCESSIBILITY NOTES
 *
 * - Blocking gates are rendered as a semantic text list.
 * - Generation/load failures use alert semantics.
 * - Package loading uses aria-live status semantics.
 * - Native select and button controls are keyboard accessible.
 * - Immutability is communicated in text rather than visually alone.
 */

export default ClosePackagePanel;
